import {DotsLoadingIndicator} from '#/atoms/dots-loading-indicator';
import {Modal} from '#/atoms/modal';
import {VerticalSeparator} from '#/atoms/vertical-separator';
import {WarningMessageAction} from '#/atoms/warning-message-action';
import {updateBrazeUserFromUserProfile} from '#/braze/update-braze-user-from-user-profile';
import {TokenSelectionButton} from '#/components/buttons/token-selection-button';
import {FormLabelInput} from '#/components/user/form-label-input';
import {UserAvatarImage} from '#/components/user/user-avatar-image';
import {ModalContext} from '#/contexts/modal-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import {baseKeysUseQuery} from '#/hooks/query/base/base-keys-use-query';
import {useInvalidateQueries} from '#/hooks/query/base/use-invalidate-queries';
import {useUserProfilePrivate} from '#/hooks/query/use-user-profile';
import {useReadLocalImage} from '#/hooks/use-read-local-image';
import {useUserAvatar} from '#/hooks/use-user-avatar';
import {useUserDisplayName} from '#/hooks/use-user-display-name';
import {useFormInputData} from '#/hooks/user-form-input-data';
import {userProfileRequests} from '#/repository/fetch-user-profile';
import {CloudArrowUpIcon, XMarkIcon} from '@heroicons/react/24/solid';
import {UserProfile, UserProfilePrivate} from '@shareland/shareland-common';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Dispatch, FormEvent, FunctionComponent, SetStateAction, useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {BaseError} from 'viem';

export const UserProfileEditModal: FunctionComponent = () => {
  const {t} = useTranslation('translation');

  const {close} = useContext(ModalContext);

  return (
    <Modal title={t('pages.editUserProfile.title')} onClose={close}>
      <div className='p-6 bg-graybluedark2 min-w-[340px] max-w-full sm:max-w-[340px] sm:min-h-[520px] flex-center'>
        <UserProfileEditModalContent />
      </div>
    </Modal>
  );
};

export const UserProfileEditModalContent: FunctionComponent = () => {
  const {t} = useTranslation('translation');

  const {data: userProfile, isFetching, isError, error, refetch} = useUserProfilePrivate();

  if (isFetching) {
    return (
      <DotsLoadingIndicator className='w-full h-full px-6 text-white text-center break-words'>
        {{
          title: t('pages.editUserProfile.loading.title'),
          subtitle: t('pages.editUserProfile.loading.subtitle'),
        }}
      </DotsLoadingIndicator>
    );
  }

  if (isError && error) {
    return (
      <WarningMessageAction
        className='h-fit space-y-2'
        iconStrokeClassName='stroke-downred'
        message={
          <div>
            <span className='block'>{t('pages.editUserProfile.error.message')}</span>
            <span className='block text-downred'>
              {error instanceof BaseError ? error.shortMessage : error instanceof Error && error.message}
            </span>
          </div>
        }
        buttonContent={t('pages.editUserProfile.error.ctaButtonContent')}
        action={refetch}
      />
    );
  }

  if (!userProfile) {
    return (
      <WarningMessageAction
        className='h-full space-y-2'
        message={t('pages.editUserProfile.empty.message')}
        buttonContent={t('pages.editUserProfile.empty.ctaButtonContent')}
        action={refetch}
      />
    );
  }

  return <UserProfileEditModalEdit userProfile={userProfile} />;
};

export const UserProfileEditModalEdit: FunctionComponent<{userProfile: UserProfilePrivate}> = ({userProfile}) => {
  const {t} = useTranslation('translation');
  const queryClient = useQueryClient();

  // Contexts
  const {close} = useContext(ModalContext);
  const {
    accountResult: {address: account},
    web3AuthSignatureResult: {data: web3AuthIdTokenGroup},
  } = useContext(WalletContext);

  // External hooks
  const invalidateQueries = useInvalidateQueries();
  const {data: userAvatar} = useUserAvatar({account, userProfile: userProfile ?? undefined});
  const {data: userDisplayName} = useUserDisplayName({
    account: account,
    userProfile: userProfile ?? undefined,
  });

  // Internal hooks
  const [userImageFile, setUserImageFile] = useState<File | null>(null);
  const isUserImageFileModified = !!userImageFile;

  const displayNameFormInputData = useFormInputData({initialValue: userProfile?.displayName});
  const emailFormInputData = useFormInputData({initialValue: userProfile?.email});
  const phoneNumberFormInputData = useFormInputData({initialValue: userProfile?.phoneNumber});

  const [displayInFiat, setDisplayInFiat] = useState(userProfile?.displayInFiat ?? true);
  const displayInFiatFormInputData = {
    value: displayInFiat,
    isModified: displayInFiat !== (userProfile?.displayInFiat ?? true),
    onChange: setDisplayInFiat,
    reset: () => {
      setDisplayInFiat(userProfile?.displayInFiat ?? true);
    },
  };

  // Vars
  const updateUserProfileMutation = useMutation({
    mutationKey: ['edit-user-profile-mutation'],
    mutationFn: async () => {
      if (!account || !web3AuthIdTokenGroup) {
        throw new Error('Account or web3AuthIdTokenGroup is not defined.');
      }

      const updatedUserProfile: Partial<UserProfile> = {
        ...(displayNameFormInputData.isModified && {displayName: displayNameFormInputData.value}),
        ...(emailFormInputData.isModified && {email: emailFormInputData.value}),
        ...(phoneNumberFormInputData.isModified && {phoneNumber: phoneNumberFormInputData.value}),
        ...(displayInFiatFormInputData.isModified && {displayInFiat: displayInFiatFormInputData.value}),
      };

      if (userImageFile) {
        const signedAvatarUrl = await userProfileRequests.signedAvatarUrl(
          {
            fileSize: userImageFile.size,
          },
          web3AuthIdTokenGroup,
        );

        const uploadResponse = await fetch(signedAvatarUrl, {
          method: 'PUT',
          body: userImageFile,
        });

        if (!uploadResponse.ok) {
          throw new SignedAvatarImageUploadCaughtError();
        }

        return await userProfileRequests.upsert(
          {
            ...updatedUserProfile,
            signedAvatarUrl,
          },
          web3AuthIdTokenGroup,
        );
      } else {
        return await userProfileRequests.upsert(updatedUserProfile, web3AuthIdTokenGroup);
      }
    },
    onSuccess(data) {
      queryClient.setQueryData([baseKeysUseQuery.useUserProfilePrivate, data.account], data);

      updateBrazeUserFromUserProfile(data);
    },
  });

  const isChanged =
    isUserImageFileModified ||
    displayNameFormInputData.isModified ||
    emailFormInputData.isModified ||
    phoneNumberFormInputData.isModified ||
    displayInFiatFormInputData.isModified;

  const isLoading = updateUserProfileMutation.isPending;
  const canSubmit = isChanged;
  const isSubmitButtonDisabled = !canSubmit || isLoading;

  const runOnce = useRef(false);
  useEffect(() => {
    if (
      !runOnce.current &&
      account &&
      !!updateUserProfileMutation.data &&
      updateUserProfileMutation.status === 'success'
    ) {
      runOnce.current = true;
      invalidateQueries.useUserProfile(account);
      close();
    }
  }, [account, close, invalidateQueries, updateUserProfileMutation.data, updateUserProfileMutation.status]);

  // Functions
  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!canSubmit || isLoading) {
      return;
    }

    await updateUserProfileMutation.mutateAsync();
  };

  return (
    <form className='w-full flex flex-col items-center justify-center gap-4' onSubmit={onSubmit}>
      <EditUserProfileAvatar
        userImageFile={userImageFile}
        setUserImageFile={setUserImageFile}
        userAvatar={userAvatar}
        userDisplayName={userDisplayName}
      />

      <FormLabelInput
        type='text'
        label={t('pages.editUserProfile.fields.displayName.label')}
        placeholder={t('pages.editUserProfile.fields.displayName.placeholder')}
        inputDataValue={displayNameFormInputData}
      />
      <FormLabelInput
        type='email'
        label={t('pages.editUserProfile.fields.email.label')}
        placeholder={t('pages.editUserProfile.fields.email.placeholder')}
        inputDataValue={emailFormInputData}
      />
      <FormLabelInput
        type='tel'
        label={t('pages.editUserProfile.fields.phoneNumber.label')}
        placeholder={t('pages.editUserProfile.fields.phoneNumber.placeholder')}
        inputDataValue={phoneNumberFormInputData}
      />

      <div className='w-full'>
        <div className='flex justify-between ml-1 mb-1.5'>
          <label className='w-full block text-gray-300 text-xs italic font-semibold'>
            {t('pages.editUserProfile.fields.currencyDisplay.label')}
          </label>
        </div>

        <div className='flex gap-2'>
          <TokenSelectionButton selected={displayInFiat} onClick={() => displayInFiatFormInputData.onChange(true)}>
            {t('basics.usd')}
          </TokenSelectionButton>

          <TokenSelectionButton selected={!displayInFiat} onClick={() => displayInFiatFormInputData.onChange(false)}>
            {t('basics.usdc')}
          </TokenSelectionButton>
        </div>
      </div>

      <VerticalSeparator my='my-1' />

      {updateUserProfileMutation.error instanceof Error && (
        <div className='w-portfolioEditModal text-center text-downred text-sm font-semibold'>
          {updateUserProfileMutation.error instanceof SignedAvatarImageUploadCaughtError
            ? t('pages.editUserProfile.errors.couldntUploadImage')
            : updateUserProfileMutation.error.message}
        </div>
      )}

      <button
        type='submit'
        className={
          'w-full mt-0 px-4 py-2 text-white rounded flex-center bg-deepblue hover:opacity-70 disabled:opacity-50 transition-all duration-200 ease-in-out'
        }
        disabled={isSubmitButtonDisabled}
      >
        {isLoading ? t('statuses.loading') : t('actions.submit')}
      </button>
    </form>
  );
};

class SignedAvatarImageUploadCaughtError extends Error {}

const EditUserProfileAvatar: FunctionComponent<{
  userImageFile: File | null;
  setUserImageFile: Dispatch<SetStateAction<File | null>>;
  userAvatar: string;
  userDisplayName: string;
}> = ({userImageFile, setUserImageFile, userAvatar, userDisplayName}) => {
  const userImageFileSrc = useReadLocalImage({imageFile: userImageFile});

  const onImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setUserImageFile(file);
  };

  const onImageClear = () => {
    setUserImageFile(null);
  };

  return (
    <div className='relative group/user-avatar'>
      <UserAvatarImage avatarUrl={userImageFileSrc ?? userAvatar} displayName={userDisplayName} widthClassName='w-28' />
      <div
        className={`w-full h-full absolute top-0 left-0 rounded-full overflow-hidden ${
          !!userImageFileSrc ? 'opacity-0 group-hover/user-avatar:opacity-100' : 'group-hover/user-avatar:opacity-90'
        } transition-opacity duration-200`}
      >
        <label
          htmlFor='dropzone-file'
          className='flex flex-col items-center justify-center w-full h-full cursor-pointer bg-gray-50/60'
        >
          <CloudArrowUpIcon className='w-8 h-8 fill-gray-400 stroke-transparent' />
          <input id='dropzone-file' type='file' className='hidden' onChange={onImageChange} />
        </label>
      </div>
      {userImageFile && (
        <button className='absolute z-20 top-0 right-0 p-2 rounded-full bg-white shadow-lg' onClick={onImageClear}>
          <XMarkIcon className='w-4 h-4 fill-black stroke-black' />
        </button>
      )}
    </div>
  );
};
